/**
 * StimaPersonalizzaDemo — sezione interattiva sulla landing.
 *
 * Mostra che TUTTO (volantino stima, vetrina, annuncio, post social) è
 * personalizzato col logo, i colori e i testi dell'agente. L'utente
 * "gioca" coi controlli e vede il preview cambiare in real-time.
 *
 * Sotto, riga finale che spiega: il proprietario può fare la stima anche
 * online in anonimo (prima riceve un range, poi sblocca il dettaglio
 * lasciando i contatti).
 *
 * iter v31 r52 — User: «aggiungi il discorso che è tutto personalizzato
 * con logo e colori e testi, esempio interattivo del volantino stima,
 * specifica anche che c'è online anonimo. Sotto alla filosofia».
 */
import React, { useEffect, useRef, useState } from "react";
import StaticTemplatePreview from "@/components/StaticTemplatePreview";
import { CANONICAL_COLOR_ENTRIES } from "@/components/staticTemplate/tokens";
import { Sparkle, Globe } from "@phosphor-icons/react";

// Tutti i 15 colori canonical brand, mostrati come swatch compatti.
// Etichette accorciate per non rompere la griglia.
const COLOR_LABELS = {
  verde_salvia: "Verde Pronto",
  verde_tenue: "Salvia",
  giallo_pastello: "Giallo",
  beige_sabbia: "Sabbia",
  terracotta_chiaro: "Terracotta chiaro",
  terracotta_classico: "Terracotta",
  rosso_polvere: "Rosso polvere",
  rosa_cipria: "Rosa cipria",
  lavanda_polvere: "Lavanda",
  blu_polvere: "Blu polvere",
  grigio_perla: "Grigio",
  marrone_scuro: "Marrone",
  bianco: "Bianco",
  nero: "Nero",
  notte: "Notte (oro)",
};
const COLOR_OPTIONS = CANONICAL_COLOR_ENTRIES.map(([key, c]) => ({
  key,
  label: COLOR_LABELS[key] || c.name,
  swatch: c.swatch,
}));

// 4 layout disponibili nel codice (vedi staticTemplate/tokens.js).
const LAYOUT_OPTIONS = [
  { key: "elegante", label: "Elegante" },
  { key: "moderno",  label: "Moderno" },
  { key: "minimal",  label: "Minimal" },
  { key: "luxury",   label: "Lussuoso" },
];

export default function StimaPersonalizzaDemo() {
  const [color, setColor] = useState("verde_salvia");
  const [layout, setLayout] = useState("elegante");
  const [agencyName, setAgencyName] = useState("La tua agenzia");
  const [agentName, setAgentName] = useState("Andrea Bianchi");
  const [city, setCity] = useState("Milano · Brera");
  const [flipped, setFlipped] = useState(false);

  // Auto-flip fronte/retro ogni ~4s. L'utente può "fermare" il flip
  // toccando il volantino → toggle manuale + pausa di 5s.
  const pausedRef = useRef(false);
  useEffect(() => {
    const t = setInterval(() => {
      if (!pausedRef.current) setFlipped((f) => !f);
    }, 4000);
    return () => clearInterval(t);
  }, []);

  const onTapPreview = () => {
    pausedRef.current = true;
    setFlipped((f) => !f);
    setTimeout(() => { pausedRef.current = false; }, 5000);
  };

  // Props passate a StaticTemplatePreview. `property.title` non serve per
  // il volantino stima (mostra invece "Stima della tua casa"). `city` va
  // in `property.city`.
  const previewProps = {
    template: "volantino_stima",
    layout,
    color,
    property: { title: "Stima", city, photos: [] },
    agency: {
      name: agencyName || "La tua agenzia",
      agent_name: agentName || "Il tuo nome",
      phone: "+39 333 000 0000",
      email: "info@tuaagenzia.it",
      slug: "demo",
    },
    showWatermark: false,
  };

  return (
    <section
      id="personalizza-demo"
      className="py-14 sm:py-20 bg-white relative overflow-hidden"
      data-testid="feature-personalizza-demo"
    >
      <div className="relative max-w-6xl mx-auto px-5 sm:px-8 lg:px-10">
        {/* Header */}
        <div className="max-w-3xl">
          <span className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#7C8B6E]">
            Tutto col tuo marchio
          </span>
          <h2 className="font-editorial text-[28px] sm:text-[38px] lg:text-[44px] mt-3 leading-[1.05] tracking-tighter text-[#1C1C1A]">
            Logo, colori, testi.
            <br />
            <span className="italic text-[#7C8B6E]">Cambi tutto. Vedi tutto.</span>
          </h2>
          <p className="mt-4 text-[14.5px] sm:text-[15.5px] text-[#3F3D38] leading-relaxed">
            Sotto vedi un esempio dal vivo: il volantino di stima per i tuoi
            potenziali venditori. Cambia colore, layout, nome, città. Si
            aggiorna mentre digiti. Gli stessi colori e gli stessi font
            valgono per annuncio online, vetrina, post social e tutto il
            resto.
          </p>
        </div>

        {/* Demo split */}
        <div
          className="mt-9 sm:mt-12 grid grid-cols-1 lg:grid-cols-12 gap-7 sm:gap-9"
          data-testid="personalizza-demo-grid"
        >
          {/* PREVIEW — sinistra (5/12 su desktop, full mobile) */}
          <div className="lg:col-span-5">
            <div className="sticky top-24">
              <div
                className="relative mx-auto"
                style={{
                  perspective: "1500px",
                  width: "min(100%, 360px)",
                  aspectRatio: "148 / 210", // A5 ratio
                }}
              >
                <button
                  type="button"
                  onClick={onTapPreview}
                  aria-label={flipped ? "Mostra fronte" : "Mostra retro"}
                  className="block w-full h-full relative cursor-pointer focus:outline-none"
                  style={{
                    transformStyle: "preserve-3d",
                    transition: "transform 700ms cubic-bezier(0.4, 0.0, 0.2, 1)",
                    transform: flipped ? "rotateY(180deg)" : "rotateY(0deg)",
                  }}
                  data-testid="personalizza-preview-flip"
                >
                  {/* Front */}
                  <div
                    className="absolute inset-0 rounded-lg overflow-hidden shadow-[0_24px_64px_-16px_rgba(28,28,26,0.28)]"
                    style={{ backfaceVisibility: "hidden", WebkitBackfaceVisibility: "hidden" }}
                  >
                    <StaticTemplatePreview {...previewProps} side="front" />
                  </div>
                  {/* Back */}
                  <div
                    className="absolute inset-0 rounded-lg overflow-hidden shadow-[0_24px_64px_-16px_rgba(28,28,26,0.28)]"
                    style={{
                      backfaceVisibility: "hidden",
                      WebkitBackfaceVisibility: "hidden",
                      transform: "rotateY(180deg)",
                    }}
                  >
                    <StaticTemplatePreview {...previewProps} side="back" />
                  </div>
                </button>
              </div>
              <p className="mt-3 text-center text-[11.5px] text-[#6B6A65]">
                Tocca il volantino per girarlo · fronte e retro
              </p>
            </div>
          </div>

          {/* CONTROLS — destra (7/12) */}
          <div className="lg:col-span-7 space-y-7">
            {/* Colore — 15 swatch piccolissimi, una riga su mobile */}
            <div data-testid="personalizza-color-picker">
              <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#5A6A4D] mb-3">
                Colore brand
              </div>
              <div className="flex flex-wrap gap-1.5">
                {COLOR_OPTIONS.map((c) => {
                  const active = c.key === color;
                  return (
                    <button
                      key={c.key}
                      type="button"
                      onClick={() => setColor(c.key)}
                      title={c.label}
                      aria-label={c.label}
                      className={`relative shrink-0 aspect-square w-7 h-7 sm:w-8 sm:h-8 rounded-full border transition-all ${
                        active
                          ? "border-[#1C1C1A] scale-110 shadow-[0_3px_8px_rgba(28,28,26,0.22)]"
                          : "border-white/80 hover:scale-105 shadow-[0_1px_2px_rgba(28,28,26,0.18)]"
                      }`}
                      style={{ background: c.swatch }}
                      data-testid={`color-swatch-${c.key}`}
                    >
                      {active && (
                        <span
                          className="absolute inset-0 rounded-full ring-2 ring-offset-1 ring-[#1C1C1A] ring-offset-white pointer-events-none"
                          aria-hidden
                        />
                      )}
                    </button>
                  );
                })}
              </div>
              <div className="mt-2 text-[11px] text-[#6B6A65]">
                {COLOR_OPTIONS.find((c) => c.key === color)?.label}
              </div>
            </div>

            {/* Layout */}
            <div data-testid="personalizza-layout-picker">
              <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#5A6A4D] mb-3">
                Stile
              </div>
              <div className="flex flex-wrap gap-2">
                {LAYOUT_OPTIONS.map((l) => {
                  const active = l.key === layout;
                  return (
                    <button
                      key={l.key}
                      type="button"
                      onClick={() => setLayout(l.key)}
                      className={`px-4 py-2 rounded-full border text-[12.5px] font-medium transition-all ${
                        active
                          ? "border-[#1C1C1A] bg-[#1C1C1A] text-white"
                          : "border-[#E5E7E0] bg-white text-[#1C1C1A] hover:border-[#7C8B6E]/50"
                      }`}
                      data-testid={`layout-option-${l.key}`}
                    >
                      {l.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Testi */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5" data-testid="personalizza-text-inputs">
              <Field label="Nome agenzia" value={agencyName} onChange={setAgencyName} testid="input-agency" />
              <Field label="Il tuo nome" value={agentName} onChange={setAgentName} testid="input-agent" />
              <Field label="Città / zona" value={city} onChange={setCity} testid="input-city" className="sm:col-span-2" />
            </div>

            {/* Hint multi-output */}
            <div className="flex items-start gap-3 p-3.5 rounded-xl bg-[#F5F2EB] border border-[#E5E7E0]">
              <Sparkle size={18} weight="fill" className="text-[#7C8B6E] mt-0.5 shrink-0" />
              <p className="text-[12.5px] text-[#3F3D38] leading-relaxed">
                Quello che vedi qui vale per <strong>tutto</strong>: la
                vetrina online dell&apos;immobile, i post Instagram, le storie,
                gli annunci sui portali. Carichi logo e colori una volta,
                Pronto li applica ovunque.
              </p>
            </div>
          </div>
        </div>

        {/* Banner — stima online anonima (1 card sola, chiara) */}
        <div
          className="mt-12 sm:mt-14 bg-[#FAFAF7] rounded-xl border border-[#E5E7E0] p-5 sm:p-7 flex items-start gap-4"
          data-testid="stima-online-anonima"
        >
          <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center shrink-0 shadow-sm">
            <Globe size={20} weight="regular" className="text-[#5A6A4D]" />
          </div>
          <div className="min-w-0">
            <div className="font-editorial text-[18px] sm:text-[20px] text-[#1C1C1A] leading-tight">
              Anche online, anonima. Il contatto arriva solo se lo vuole il cliente.
            </div>
            <p className="mt-2 text-[13.5px] sm:text-[14px] text-[#3F3D38] leading-relaxed">
              Il proprietario apre il tuo link <strong>prontoimmo.it/stima/{`{tuo-codice}`}</strong>
              dal telefono. Risponde a 6 domande sull&apos;immobile. Riceve
              subito un range di stima <strong>senza lasciare nome o contatti</strong>.
              Se vuole una valutazione più accurata (cedolare, comparabili,
              scenario di vendita), ti lascia il numero. A quel punto il lead
              è in rubrica per te.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

// Campo di input controllato — etichetta sopra, input pulito.
function Field({ label, value, onChange, testid, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#5A6A4D]">
        {label}
      </span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full px-3.5 py-2.5 rounded-lg border border-[#E5E7E0] bg-white text-[14px] text-[#1C1C1A] focus:outline-none focus:border-[#7C8B6E] focus:ring-2 focus:ring-[#7C8B6E]/20 transition-all"
        data-testid={testid}
      />
    </label>
  );
}
